import type {
  UseHeldCallsLogicParams,
  UseHeldCallsLogicReturn,
} from './index.types';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type { CallSession } from 'Modules/softphone/types';
import { useMemo, useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { pushToGTMDataLayer } from '~/utils/analytics';

/**
 * Sort calls by duration (longest first)
 */
function sortCallsByDuration(calls: CallSession[]): CallSession[] {
  return [...calls].sort((a, b) => b.duration - a.duration);
}

/**
 * Hook to manage held calls list logic including sorting, expansion state, and swap handling
 *
 * @param params - Hook parameters including holding calls, current call, and swap handler
 * @returns Sorted calls, display flags, expansion state, and event handlers
 */
export function useHeldCallsLogic({
  holdingCalls,
  currentCall,
  onSwapCall,
}: UseHeldCallsLogicParams): UseHeldCallsLogicReturn {
  const { isExpanded, toggleCollapsible, closeCollapsible } = useSoftphoneStore(
    useShallow((state) => ({
      isExpanded: state.expandedCollapsible === 'heldCalls',
      toggleCollapsible: state.toggleCollapsible,
      closeCollapsible: state.closeCollapsible,
    }))
  );

  // Sort calls by duration (longest first)
  const sortedCalls = useMemo(
    () => sortCallsByDuration(holdingCalls),
    [holdingCalls]
  );

  // Determine display mode
  const isSingleCall = sortedCalls.length === 1;
  const isMultipleCalls = sortedCalls.length > 1;

  // Handle Escape key to collapse list
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isExpanded) {
        closeCollapsible();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded, closeCollapsible]);

  // Handle call swap with analytics tracking
  const handleSwapCall = useCallback(
    (callId: string) => {
      pushToGTMDataLayer('on_call_swap', {
        callId,
        fromCallId: currentCall?.id || '',
        totalHoldingCalls: holdingCalls.length,
      });

      onSwapCall(callId);
      closeCollapsible();
    },
    [onSwapCall, currentCall, holdingCalls.length, closeCollapsible]
  );

  const handleToggle = useCallback(() => {
    toggleCollapsible('heldCalls');
  }, [toggleCollapsible]);

  return {
    sortedCalls,
    isSingleCall,
    isMultipleCalls,
    isExpanded,
    handleSwapCall,
    handleToggle,
    closeCollapsible,
  };
}
